import { useEffect, useRef, useState } from 'react'
import { KEYBOARD_KEY, DOM_EVENT } from '@/shared/constants/keyboard'
import {
  ANIMATION_DURATION_MS,
  MOUNT_DELAY_MS,
} from '@/shared/constants/timings'

export function useModal(
  isOpen: boolean,
  onClose: () => void,
  keepMounted?: boolean,
) {
  const [mounted, setMounted] = useState(isOpen)
  const [visible, setVisible] = useState(false)
  const [hidden, setHidden] = useState(!isOpen)
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current)

    if (isOpen) {
      setHidden(false)
      setMounted(true)
      timerRef.current = setTimeout(() => setVisible(true), MOUNT_DELAY_MS)
    } else {
      setVisible(false)
      if (!keepMounted) {
        timerRef.current = setTimeout(
          () => setMounted(false),
          ANIMATION_DURATION_MS,
        )
      } else {
        timerRef.current = setTimeout(
          () => setHidden(true),
          ANIMATION_DURATION_MS,
        )
      }
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [isOpen, keepMounted])

  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === KEYBOARD_KEY.ESCAPE) onClose()
    }
    document.addEventListener(DOM_EVENT.KEYDOWN, handleKeyDown)
    return () => document.removeEventListener(DOM_EVENT.KEYDOWN, handleKeyDown)
  }, [isOpen, onClose])

  useEffect(() => {
    if (!isOpen) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [isOpen])

  return { mounted, visible, hidden }
}
